/**
 * idleAssetWarmup.js — 登录后空闲时段静默预热
 *
 * ══════════════════════════════════════════════════════════════════
 * 🎯 解决了什么问题？
 *
 *   首屏渲染时如果同步去拉缩略图、字幕模型、ffmpeg-core 这些静态资源，
 *   会和 /api/projects、/api/auth 抢带宽，让真正阻塞用户的请求变慢。
 *
 *   这个 service 把所有「可以晚点儿做」的下载推迟到：
 *     ① 登录完成 8s 之后
 *     ② 页面可见、网络在线、不是省流模式
 *     ③ 用户最近 4s 没操作（避开滚动/打字）
 *     ④ 浏览器进入 requestIdleCallback 空闲帧
 *   全部满足时才发请求，且 4 并发限流。
 *
 * ══════════════════════════════════════════════════════════════════
 * 📋 当前预热任务列表（按优先级排序，串行执行）
 *
 *   1. project-thumbnails  图板封面缩略图  ← 影响首页首屏体验
 *   2. asset-thumbnails    素材库缩略图    ← 影响素材抽屉首次打开速度
 *   3. ffmpeg-core         视频快剪 wasm   ← 进入快剪前预热
 *   4. bg-removal          抠图模型 wasm   ← 进入抠图前预热
 *
 *   每个任务跑完调 completedTasks.add() 标记已完成，整个会话不再重复跑。
 *   登出时 resetIdleAssetWarmupState() 清空，下个用户重新预热。
 *
 * ══════════════════════════════════════════════════════════════════
 * 🔌 触发入口
 *
 *   ProtectedRoute.js useEffect → startIdleAssetWarmup()
 *   AuthContext.logout()       → resetIdleAssetWarmupState()
 *
 * ══════════════════════════════════════════════════════════════════
 * ⚠️ 修改注意事项
 *
 *   - 新增任务时放进 getPendingTasks() 即可；run() 必须返回
 *     { success: true } 才会被记录为完成。
 *   - 不要让任务把异常抛出去 —— runTask 已有 try/catch，但抛了会让
 *     下游 task 沿着 sleep(TASK_GAP_MS) 等更久。
 *   - 缩略图预热依赖 localCacheService.getLocalUrlOrFetch 的命中检查，
 *     重复运行成本极低；如果改了那个函数的命中策略要重新评估。
 * ══════════════════════════════════════════════════════════════════
 */

import localCacheService from './localCacheService';
import dashboardCacheService from './dashboardCacheService';
import { API_BASE_URL } from '../constants';

// ── 静态资源 base URL（FFmpeg 和抠图模型走 Service Worker 缓存）──
const BG_REMOVAL_BASE_URL = `${window.location.origin}/bg-removal/`;
const FFMPEG_BASE_URL = `${window.location.origin}/ffmpeg-core/`;

// ── 时间常量 ──
const START_DELAY_MS = 8000;     // 登录后等多久再启动预热
const QUIET_WINDOW_MS = 4000;    // 用户安静多久才允许跑任务
const TASK_GAP_MS = 1200;        // 任务之间的间隔，留给主线程喘气
const IDLE_TIMEOUT_MS = 20000;   // requestIdleCallback 兜底超时

// ── 缩略图并发上限 ──
// 4 = 既能利用 HTTP/2 多路复用，又不会让 Express rateLimit 误伤。
// 想再激进可改 6，再保守可改 2。
const THUMBNAIL_CONCURRENCY = 4;

const USER_ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'];

// ── 模块级状态 ──
let trackersInstalled = false;
let lastUserActivityAt = Date.now();
let activeRunPromise = null;
const completedTasks = new Set();

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function markUserActivity() {
  lastUserActivityAt = Date.now();
}

function installUserActivityTrackers() {
  if (trackersInstalled || typeof window === 'undefined') return;
  trackersInstalled = true;
  USER_ACTIVITY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, markUserActivity, { passive: true, capture: true });
  });
}

async function waitForPageVisible() {
  if (typeof document === 'undefined' || !document.hidden) return;
  await new Promise((resolve) => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        resolve();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
  });
}

async function waitForOnline() {
  if (typeof navigator === 'undefined' || navigator.onLine !== false) return;
  await new Promise((resolve) => {
    const handleOnline = () => {
      window.removeEventListener('online', handleOnline);
      resolve();
    };
    window.addEventListener('online', handleOnline);
  });
}

function shouldSkipForNetwork() {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!connection) return false;
  if (connection.saveData) return true;
  return typeof connection.effectiveType === 'string' && connection.effectiveType.includes('2g');
}

async function waitForQuietWindow() {
  while (Date.now() - lastUserActivityAt < QUIET_WINDOW_MS) {
    await sleep(500);
  }
}

async function waitForIdleSlot() {
  await new Promise((resolve) => {
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(() => resolve(), { timeout: IDLE_TIMEOUT_MS });
      return;
    }
    setTimeout(resolve, 600);
  });
}

async function runTask(task) {
  await waitForPageVisible();
  await waitForOnline();
  await waitForQuietWindow();
  await waitForIdleSlot();

  const result = await task.run();
  if (result?.success) {
    completedTasks.add(task.id);
    console.info(`[idleAssetWarmup] ${task.label} 预热完成`);
  } else {
    console.info(`[idleAssetWarmup] ${task.label} 预热跳过`, result?.reason || 'unknown');
  }
}

// ────────────────────────────────────────────────────────────────────
// 任务清单
// ────────────────────────────────────────────────────────────────────
// 顺序敏感：缩略图直接影响首屏体验，必须在 wasm 模型之前；
// 同 id 的任务整个 session 只跑一次（completedTasks 去重）。
function getPendingTasks() {
  return [
    {
      id: 'project-thumbnails',
      label: '图板缩略图',
      // 拉一次 /api/projects → 把每张 thumbnail URL 喂给 getLocalUrlOrFetch。
      // 顺手把 projects 列表写入 dashboardCacheService（warmThumbnailsFromList 内部）。
      run: () => warmThumbnailsFromList('/api/projects?limit=100', (data) => {
        const list = Array.isArray(data) ? data : (data.projects || []);
        return list
          .filter(p => p.thumbnail)
          .map(p => ({ url: p.thumbnail, crewId: p.crewId, type: 'image' }));
      }),
    },
    {
      id: 'asset-thumbnails',
      label: '素材库缩略图',
      // 角色库 / 道具库 / 场景库等共享 /api/assets。
      // 同时预热缩略图和主图（image 类型素材），避免点开素材抽屉时还要再下一次。
      // 字段名兼容 camelCase 与 snake_case（不同接口风格）。
      run: () => warmThumbnailsFromList('/api/assets', (data) => {
        const list = Array.isArray(data) ? data : (data.assets || []);
        return list
          .flatMap(a => {
            const out = [];
            if (a.thumbnailUrl || a.thumbnail_url) {
              out.push({ url: a.thumbnailUrl || a.thumbnail_url, crewId: a.crewId || a.crew_id, type: 'image' });
            }
            if (a.url && (!a.type || a.type === 'image')) {
              out.push({ url: a.url, crewId: a.crewId || a.crew_id, type: 'image' });
            }
            return out;
          });
      }),
    },
    {
      id: 'ffmpeg-core',
      label: 'FFmpeg Core',
      run: () => localCacheService.prepareFfmpegAssets(FFMPEG_BASE_URL),
    },
    {
      id: 'bg-removal',
      label: 'Bg Removal',
      run: () => localCacheService.warmBgRemovalAssets(BG_REMOVAL_BASE_URL),
    },
  ].filter(task => !completedTasks.has(task.id));
}

// ────────────────────────────────────────────────────────────────────
// 缩略图后台预热
// ────────────────────────────────────────────────────────────────────
//
// 流程：
//   1) 拉列表 API（带 token）
//   2) 顺手把列表 JSON 写入 dashboardCacheService（首页/抽屉秒开复用）
//   3) mapper 提取所有要预热的 (url, crewId, type) 三元组
//   4) THUMBNAIL_CONCURRENCY 个 worker 并发消费队列
//      - 用户活跃时 sleep 800ms 让路（再次礼让，避免抢主请求带宽）
//      - getLocalUrlOrFetch 内部已做 IndexedDB 命中检查，重复调用零成本
//
// 失败策略：
//   - 网络/HTTP 错误 → 整个任务返回 { success: false }，下次预热再试
//   - 单张图失败 → 默默跳过，不影响其他图
//
// @param endpoint  形如 '/api/projects?limit=100'
// @param mapper    (data) => Array<{ url, crewId, type }>
async function warmThumbnailsFromList(endpoint, mapper) {
  const token = _getAuthToken();
  if (!token) return { success: false, reason: 'no-token' };

  let data;
  try {
    const resp = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) return { success: false, reason: `http-${resp.status}` };
    data = await resp.json();
  } catch (e) {
    return { success: false, reason: e.message };
  }

  // 顺手写入 dashboard 列表快照（首页 stale-while-revalidate 立刻可用）。
  // 目前只为 /api/projects 缓存；assets 由 AssetDrawer 自行写入更准确。
  try {
    const userId = localCacheService.getCurrentUserId();
    if (userId && endpoint.startsWith('/api/projects')) {
      const list = Array.isArray(data) ? data : (data.projects || []);
      dashboardCacheService.put(dashboardCacheService.keys.projects(userId), list);
    }
  } catch (_) {}

  const targets = mapper(data);
  if (!targets.length) return { success: true };

  // 简单的"工作池"模式：N 个 worker 共享一个队列，先到先抢。
  // 比 Promise.all + chunk 更平滑（不会出现一批快、一批慢的情况）。
  const queue = targets.slice();
  const workers = Array.from({ length: Math.min(THUMBNAIL_CONCURRENCY, queue.length) }, async () => {
    while (queue.length) {
      // 用户活跃时主动让路。和 waitForQuietWindow 的区别：
      //   - waitForQuietWindow 在任务开始前等一次
      //   - 这里在每张图之间都检查，更细粒度
      if (Date.now() - lastUserActivityAt < QUIET_WINDOW_MS) {
        await sleep(800);
        continue;
      }
      const t = queue.shift();
      try {
        await localCacheService.getLocalUrlOrFetch(t.url, t.crewId, undefined, t.type);
      } catch (_) {
        // 单张失败不影响整体（图可能已被删/服务器临时 5xx 等）
      }
    }
  });
  await Promise.all(workers);
  return { success: true };
}

/**
 * 读取当前会话的 JWT token
 * 与 localCacheService 保持一致：会话模式（管理员代登录到用户）使用
 * sessionStorage，普通登录使用 localStorage。
 */
function _getAuthToken() {
  if (sessionStorage.getItem('cineart_session_mode')) {
    return sessionStorage.getItem('cineart_token');
  }
  return localStorage.getItem('cineart_token');
}

/**
 * 启动预热（幂等）
 * 已在跑就返回同一个 Promise，不会启第二条流水线。
 * 由 ProtectedRoute 在 isAuthenticated && !loading 时调用。
 */
export function startIdleAssetWarmup() {
  installUserActivityTrackers();

  if (activeRunPromise) return activeRunPromise;

  activeRunPromise = (async () => {
    await sleep(START_DELAY_MS);

    if (shouldSkipForNetwork()) {
      console.info('[idleAssetWarmup] 当前网络策略为节省流量/弱网，跳过系统资源静默预热');
      return;
    }

    const pendingTasks = getPendingTasks();
    if (pendingTasks.length === 0) return;

    for (const task of pendingTasks) {
      try {
        await runTask(task);
      } catch (error) {
        console.warn(`[idleAssetWarmup] ${task.label} 预热失败:`, error?.message || error);
      }
      await sleep(TASK_GAP_MS);
    }
  })().finally(() => {
    activeRunPromise = null;
  });

  return activeRunPromise;
}

/**
 * 重置预热状态（登出时调用）
 * 让下一个登录的用户能重新跑一次预热（不同账号缩略图集合不同）。
 */
export function resetIdleAssetWarmupState() {
  completedTasks.clear();
  activeRunPromise = null;
  lastUserActivityAt = Date.now();
}